<template>
  <div v-if="isDriver" class="driver" @click.once="sendTestNotification">
    <MainBackButton />
    <DriverPanel2 />
    <DriverButtons />
    <CommerceOrderDriverModal />

    <div class="top-right" :key="z">
      <p class="button is-white is-rounded bullseye-btn" @click="locate" :disabled="zooming">
        <span class="icon bullseye wiggle" :class="{ 'fa-spin': zooming }" />
      </p>
      <p class="button is-white" :class="{ 'bg-green': showFleet }">
        <span class="icon" @click="toggleFleet">
          <img :src="this.icon" />
        </span>
      </p>
      <p class="button is-white has-text-link">
        <span class="icon" @click="openWallet">
          <i class="fa fa-wallet" :class="{ 'has-text-danger': missingPaymentInfo }" />
        </span>
      </p>
      <p v-if="!hasGeolocation" class="button is-danger is-rounded wiggle" @click="checkGeolocation(true)">
        <span class="icon">
          <i class="fa fa-exclamation-circle" />
        </span>
      </p>
    </div>

    <div class="map-wrapper">
      <div :id="mapID" class="map" />
    </div>

    <WorkflowStatsModal />

    <DriverWallet v-if="showWallet" :missingPaymentInfo="true" :remitTransactions="remitTransactions"
      @close="closeWallet" />
  </div>

  <div v-else class="notification">
    <i class="fa fa-kiwi-bird" />
    <p class="title has-text-info">{{ $t("driver.not-allowed") }}</p>
    <a class="button is-success" @click="goHome">{{ $t("go-home") }}</a>
  </div>
</template>


<script>
import EventBus from "../eventBus";
// import $Scriptjs from "scriptjs";
import L from "leaflet";
// import "leaflet.locatecontrol";
import "leaflet.awesome-markers";
import "leaflet.marker.slideto";
// import "leaflet-edgebuffer";

import MainBackButton from "@/components/MainBackButton";
import DriverButtons from "@/components/DriverButtons.vue";
import DriverPanel2 from "@/components/DriverPanel2.vue";
import WorkflowStatsModal from "@/components/WorkflowStatsModal.vue";
import DriverWallet from "@/components/DriverWallet.vue";
import CommerceOrderDriverModal from "@/components/CommerceOrderDriverModal.vue";

import geo from "@/geo";
import vehicles from "@/vehicles";
// import { sounds } from "../functions.js";

const CachedTileLayer =
  require("@yaga/leaflet-cached-tile-layer").CachedTileLayer;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "Driver",
  components: {
    MainBackButton,
    DriverButtons,
    DriverPanel2,
    WorkflowStatsModal,
    DriverWallet,
    CommerceOrderDriverModal,
  },
  data() {
    return {
      mapID: "driver-map",
      remitTransactions: null,
      hasGeolocation: true,
      zooming: false,
      showFleet: false,
      showWallet: false,
      // locateControl: undefined,
      locateToggle: false, //true,
      map: undefined,
      stateLayer: null,
      vehiclesLayer: null,
      userMarker: null,
      driverMarker: null,
      hiddenDriverMarker: null,
      driversMarkers: [], // drivers for observers, all active drivers for workflow
      passengerMarkers: [],
      slideOpts: { duration: 10000, keepAtCenter: false },
      popupNarrow: { closeButton: false, maxWidth: 100, autoPan: false },
      popupWide: { closeButton: false, maxWidth: 300, autoPan: false },
      dashLine: {
        color: "orange",
        weight: "1",
        dashArray: "5, 5",
        lineCap: "square",
      },
      drivingLineOpt: {
        color: "blue",
        weight: "2",
        opacity: 0.7,
      },
      straightLineOpt: {
        color: "blue",
        weight: "2",
        dashArray: "3, 3",
        opacity: 0.4,
      },
      z: 0, // hack to change toggle fleet button color
    };
  },
  computed: {
    isDriver() {
      return this.$store.getters.isDriver;
    },
    role() {
      return this.$store.getters.getDriverRole;
    },
    hasUnpaidRemit() {
      return this.remitTransactions.find(
        (r) => r.state === "ready"
      );
    },
    hasDelinquentRemit() {
      // this is a hack, but it should be effective
      // I should check the oldest unpaid remit and
      // and see if it's >12 hours old
      var hour = new Date().getHours();
      if (hour < 14) return false;
      return this.hasUnpaidRemit;
    },
    missingPaymentInfo() {
      return !this.hasBankAccount || !this.hasPromptPay;
    },
    hasBankAccount() {
      return this.role.data.bank_account;
    },
    hasPromptPay() {
      return this.role.data.qr_pic_url;
    },
    workflow() {
      return this.role.workflow;
    },
    isDispatcher() {
      return this.$store.getters.isDispatcher;
    },
    jobs() {
      var transactions = this.$store.getters.getWorkerTransactions;
      return transactions.filter((t) => t.state !== "scheduled");
    },
    hasConfirmedJobs() {
      return this.$store.getters.getWorkerTransactionsStateConfirmed.length > 0;
    },
    rejectedJobs() {
      return this.$store.state.rejectedTransactions;
    },
    icon() {
      if (!this.workflow) return null;
      var iconName = this.workflow.data.icon || this.workflow.name;
      return vehicles.img(iconName);
    },
  },
  created() {
    L.AwesomeMarkers.Icon.prototype.options.prefix = "fa";
  },
  mounted() {
    this.checkDriverRequirements();
    this.checkDriverRemit();
    this.checkDriverStats();
    this.updateLocation();
    this.pollLocation(); // calls updateLocation every 15 seconds
    // this.checkVersion(); // check app version
    // this.checkGeolocation(true);
    // var pouchDBScript = `https://unpkg.com/pouchdb@^5.2.0/dist/pouchdb.js`;
    // var pouchDBScript = `https://unpkg.com/pouchdb@^7.2.2/dist/pouchdb.min.js`;
    // var layerCacheScript = `https://unpkg.com/leaflet.tilelayer.pouchdbcached@latest/L.TileLayer.PouchDBCached.js`;
    // this.$scriptjs([pouchDBScript, layerCacheScript], () => {
    //   // this.$scriptjs(layerCacheScript, () => {
    //   this.initMap();
    //   this.setupMapState();
    // });
    this.initMap();
    this.setupMapState();
    this.subscribeToStore();
    this.onVisibilityChange();

    EventBus.$on("DRIVER_FIT_LAYER", () => this.fitToLayer(this.stateLayer));

    EventBus.$on("COMMERCE_ORDER_DRIVER_MODAL", () => {
      this.displayModalNext(".commerce-order-driver-modal");
    });
  },
  activated() {
    if (this.isBlockedDriver()) return;
    EventBus.$emit("DRIVER_START_WORKING");
    this.updateLocation();
    setTimeout(() => this.checkMissingPaymentInfo(), 2000);
  },
  deactivated() {
    this.showWallet = false;
  },
  methods: {
    checkDriverStats() {
      // useful stats to manage driver behaviour
      this.$store.dispatch("getDriverStats", this.userID())
        .then(res => {
          console.log("getDriverStats", res.data);
        })
        .catch(err => this.apiError(err))
    },
    checkDriverRemit() {
      this.$store
        .dispatch("getRecentDriverRemitTransactions", this.userID())
        .then((res) => {
          console.log("getRecentDriverRemit", res.data);
          this.remitTransactions = res.data || [];
          if (this.hasUnpaidRemit) this.openWallet();
        })
        .catch((err) => this.apiError(err));
    },
    isBlockedDriver() {
      if (!this.$store.state.user) {
        this.goHome();
        return true;
      }
      if (this.$store.state.user.data.block_driver) {
        var msg = `<i class='fa fa-exclamation-circle'></i> 
        ไดรเวอร์ที่ถูกบล็อก&nbsp;<i class='fa fa-exclamation-circle'></i> 
        <br>โปรดติดต่อฝ่ายสนับสนุน`;
        this.errorToast(msg, "top", 60 * 1000);
        this.goHome();
        return true;
      }
      return false;
    },
    checkDriverRequirements() {
      // drivers are required to have push notifications and GPS
      if (!this.$store.state.notificationToken) {
        // var msg = `⚠️ Driver No Notification Token [ ${this.$store.getters.getNameAndNumber} ]`;
        // this.sendSuperuserNotification(msg);
        let icon = `<i class='fa fa-exclamation-triangle'></i>&nbsp;`;
        let warning = icon + this.$t("driver.check-notification-settings");
        setTimeout(() => this.errorToast(warning, "top", 10 * 1000), 2000);
      }
      // TODO check geolocation permission
    },
    sendTestNotification() {
      var obj = this.$store.state.notification;
      if (obj && obj.created_at) return;
      if (!this.isProduction()) return;
      this.requestPushNotificationPermission();
      this.log("check_notifications", "driver");
      this.sendPushNotification("Driver Notifications ON 👍🏼", this.userID());
    },
    checkMissingPaymentInfo() {
      if (this.isSuperUser()) return; // don't warn super user
      if (!this.hasPromptPay) {
        var msg = `Upload PromptPay QR Code <i class='fa fa-qrcode'></i>`;
        this.errorToast(msg, "top", 12000);
      }
      if (!this.hasBankAccount) {
        var msg2 = `Enter Bank Account Details <i class='fa fa-university'></i>`;
        setTimeout(() => this.errorToast(msg2, "top", 12000), 1500);
      }
    },
    openWallet() {
      this.showWallet = true;
    },
    closeWallet() {
      this.showWallet = false;
      // check if has delinquent outstanding remit, and re-open wallet
      if (this.hasDelinquentRemit) {
        this.errorToast("This Account is Blocked!<br>Due to Unpaid Remit");
        if (this.isSuperUser()) return;
        setTimeout(() => this.openWallet(), 3000);
        // var msg = `⚠️ Blocked delinquent driver [ ${this.$store.getters.getNameAndNumber} ]`;
        // this.sendSuperuserNotification(msg)
      }
    },
    onVisibilityChange() {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") return;
        // this.updateTransactionsForDriver(true); // is this necessary?
        this.updateLocation();
        this.updateDriversForObserver(); // update drivers on map
      });
    },

    subscribeToStore() {
      this.$store.subscribe((mutation) => {
        if (mutation.type === "setTransactionsForWorker") {
          this.drawPassengersForDriver();
          if (this.hasConfirmedJobs) {
            //   this.toggleFleet({val: false}) // force hide vehicles
            this.removeDriversForObserver();
          } else this.updateDriversForObserver();
        }
        // draw yourself from your own userState
        var mutations = ["setUserState", "setRoles", "setWorkingState"];
        if (mutations.includes(mutation.type)) this.drawDriverForDriver(); // should update any lines
      });
    },

    initMap() {
      // var tilesURL = this.$t("geo.map-tiles"); // language specific maps
      var initialLocation = [13.73114, 100.57529]; // default: Bangkok, Soi 41
      if (this.$store.getters.getUserLocation) {
        initialLocation = this.$store.getters.getUserLocation;
      }
      var mapOptions = { attributionControl: false, doubleClickZoom: false }; // this is default anyway, but not firing on mobile safari
      this.map = L.map(this.mapID, mapOptions).setView(initialLocation, 17); // set zoom level
      this.map.removeControl(this.map.zoomControl);

      var tilesURL = ""; // set below, because iOS had a bug...

      if (this.isIOSApp()) {
        // iOS still use maptiler until can debug why it has blank map
        tilesURL = this.$t("geo.map-tiles"); // language specific maps
        new CachedTileLayer(tilesURL, {
          // edgeBufferTiles: 1,
          tileSize: 512,
          zoomOffset: -1,
          minZoom: 2,
          maxZoom: 19,
          // useCache: false,
          crossOrigin: true,
          maxAge: 1000 * 60 * 60 * 24 * 30, // optional, default is 7 days
        }).addTo(this.map);
      } else {
        // non-iOS uses google tile to save money, and better driver experience
        tilesURL = `http://{s}.google.com/vt?lyrs=m@221097413,traffic&x={x}&y={y}&z={z}&hl=${this.languageCode()}`;
        L.tileLayer(tilesURL, {
          subdomains: ["mt0", "mt1", "mt2", "mt3"], // google only
          tileSize: 512,
          zoomOffset: -1,
          minZoom: 2,
          // maxZoom: 19, // doesn't seem to need this on googlemaps
          detectRetina: true, // used for google tiles, not needed for maptiler
          crossOrigin: true,
          maxAge: 1000 * 60 * 60 * 24 * 30, // optional, default is 7 days
        }).addTo(this.map);
      }

      this.stateLayer = L.featureGroup().addTo(this.map);
      this.vehiclesLayer = L.featureGroup().addTo(this.map);

      this.map.on("zoomend", () => (this.zooming = false));
      this.map.on("moveend", () => (this.zooming = false));
      this.map.on("dblclick", () => this.locate()); //this.fitToLayer(this.stateLayer));
    },

    /* toggle between flying to your location and fitting to stateLayer */
    locate() {
      //   this.pollLocation();
      if (this.zooming) return; // don't do anything if already zooming
      this.zooming = true;
      setTimeout(() => (this.zooming = false), 3000); // fallback
      this.console("locateToggle", this.locateToggle);
      if (this.locateToggle) {
        // distance check here should be current map center to userLocation!!!
        if (this.$store.state.userState.distance > 100) {
          this.map.setView(this.$store.getters.getUserLocation);
        } else {
          this.map.flyTo(this.$store.getters.getUserLocation);
        }
      } else {
        this.fitToLayer(this.stateLayer);
      }
      this.locateToggle = !this.locateToggle; // toggle it
      setTimeout(() => this.map.invalidateSize(), 1000); // might help force redraw
    },

    fitToLayer(layer) {
      if (!layer || layer.getLayers().length === 0) return; // (this.zooming = false);
      var opts = { padding: [50, 50] }; // pixels not meters
      try {
        var dist = 0;
        if (layer.getBounds()) {
          var newCenter = layer.getBounds().getCenter();
          dist = geo.distance(newCenter, this.map.getCenter());
        }
        if (dist > 1000) this.map.fitBounds(layer.getBounds(), opts);
        else this.map.flyToBounds(layer.getBounds(), opts);
      } catch (err) {
        console.error("fitToLayer error", err);
        this.map.setView(this.userMarker.getLatLng(), 14);
        this.map.zoomIn();
      }
    },

    toggleFleet() {
      this.zooming = true;
      setTimeout(() => (this.zooming = false), 1000); // fallback
      this.showFleet = !this.showFleet;
      console.log("toggleFleet", this.showFleet);
      if (this.showFleet) {
        this.updateDriversForObserver(); // update vehicle positions
        var n = this.driversMarkers.length;
        this.infoToast(`${n} vehicles nearby`, "top");
        this.map.addLayer(this.vehiclesLayer);
        this.fitToLayer(this.vehiclesLayer); // this.map.zoomOut();
      } else {
        this.map.removeLayer(this.vehiclesLayer);
        this.fitToLayer(this.stateLayer);
      }
      this.z += 1; // force toggle fleet button color update
    },

    resetMapObjects() {
      this.driverMarker = null;
      this.userMarker = null;
      this.passengerMarkers = [];
    },

    clearLayer(layer) {
      if (layer) layer.clearLayers();
    },

    setupMapState() {
      this.clearLayer(this.stateLayer);
      this.resetMapObjects();
      this.setupMapStateDriver();
      // this.drawUserMarker(); // faint marker to keep user location in stateLayer
      this.fitToLayer(this.stateLayer);
    },

    setupMapStateDriver() {
      this.setupLocateControl();
      this.$store.commit("selectTransport", this.workflow);
      this.drawPassengersForDriver();
      this.drawDriverForDriver();
      this.updateDriversForObserver(); // show drivers their team
      // this.drawStopsForDriver();
    },

    setupLocateControl() {
      if (this.locateControl) this.map.removeControl(this.locateControl);
      var clickBehavior = {
        inView: "setView",
        outOfView: "inView", //"setView",
        inViewNotFollowing: "inView",
      };
      var options = {
        enableHighAccuracy: true,
        keepCurrentZoomLevel: true,
        flyTo: true,
        showPopup: false,
        drawMarker: true, // false for driver only
        showCompass: true, // false for driver only
        drawCircle: false,
        clickBehavior: clickBehavior,
        onLocationError: (err) => console.warn("locateControl", err),
      };
      this.locateControl = L.control.locate(options).addTo(this.map);
      this.locateControl.start(); // centers map to user geolocation, throwing an error
      var lc = this.$el.querySelector(".leaflet-control-locate");
      lc.style.visibility = "hidden"; // hide the default locate control
    },

    ditherCoords(s) {
      var dither = (Math.random() - 0.5) * 0.0002;
      return [s.lat + dither, s.lon + dither];
    },

    isDelivery(t) {
      return t.workflow_type_name === "Delivery";
    },

    coloredIcon(t) {
      var color = "blue"; // enroute, default color
      if (t.state === "initiated") color = "orange";
      if (t.state === "confirmed") color = "darkblue";
      if (t.state === "cancelled_by_passenger") color = "red";
      if (t.state === "driver_arrived") color = "green";
      var icon = "street-view";
      if (this.isDelivery(t)) icon = "store-alt";
      var opts = { icon: icon, markerColor: color };
      return L.AwesomeMarkers.icon(opts);
    },

    drawPassengerDropoffPoint(customerMarker) {
      var c = customerMarker;
      if (!c.transaction.data.dropoff_stop) return;
      var s = c.transaction.data.dropoff_stop;
      var dStr = this.$t("driver.dropoff");
      var stopName = s.name || this.$t("ride.pin-location");
      var text = `<i>${dStr}</i><br>${stopName}`;
      // var dropoffText = this.$t("transport.dropoff-point");
      // var text = `<strong>${stopName}</strong><br>${dropoffText}`;
      var dot = L.divIcon({
        html: `<div class='dropoff-point'></div>`, // yellow dot
        popupAnchor: [4, -7],
        className: "none",
      });
      var icon = { icon: dot };
      var dropoff = L.marker([s.lat, s.lon], icon)
        .addTo(this.stateLayer)
        .bindPopup(text, this.popupWide);
      c.dropoff = dropoff;
      c.point = dropoff;
    },

    drawPassengerToPickupLine(customerMarker) {
      var c = customerMarker;
      if (this.isDelivery(c.transaction)) return; // don't draw orange dashed line for delivery
      var stop = c.transaction.data.pickup_stop;
      var pickupCoords = [stop.lat, stop.lon];
      var latlngs = Array();
      if (!c.line) {
        latlngs.push(c.getLatLng());
        latlngs.push(pickupCoords);
        c.line = L.polyline(latlngs, this.dashLine).addTo(this.stateLayer);
        this.redrawLineOnZoom(c.line);
      } else {
        latlngs.push(c.getLatLng());
        latlngs.push(pickupCoords);
        c.line.setLatLngs(latlngs);
      }
    },

    drawPassengerPickupPoint(customerMarker) {
      var c = customerMarker;
      var s = c.transaction.data.pickup_stop;
      var pStr = this.$t("driver.pickup");
      var stopName = this.shortenString(s.name, 30);
      if (!stopName) stopName = this.$t("ride.pin-location");
      var text = `<i>${pStr}</i><br>${stopName}`;
      var dot = L.divIcon({
        html: `<div class='pickup-point'></div>`, // light green dot
        popupAnchor: [4, -7],
        className: "none",
      });
      if (c.transaction.state.includes("cancelled_")) {
        dot = L.divIcon({
          html: `<div class='pickup-point bg-red'></div>`, // red dot
          popupAnchor: [4, -7],
          className: "none",
        });
      }
      var icon = { icon: dot };
      var pickup = L.marker([s.lat, s.lon], icon);
      pickup.addTo(this.stateLayer).bindPopup(text, this.popupWide);
      c.pickup = pickup;
      c.point = pickup;
    },

    redrawLineOnZoom(line) {
      this.map.on("zoomend", () => line.redraw());
    },

    removeOldJob(m) {
      this.stateLayer.removeLayer(m.driverLine);
      this.stateLayer.removeLayer(m.dropoff);
      this.stateLayer.removeLayer(m.pickup);
      this.stateLayer.removeLayer(m.line);
      this.stateLayer.removeLayer(m);
    },

    removeOldJobs() {
      this.passengerMarkers.forEach((m, idx) => {
        if (!this.jobs.find((t) => t.id === m.id)) {
          this.removeOldJob(m);
          this.passengerMarkers.splice(idx, 1); // remove from array
        }
      });
      if (!this.jobs.length) {
        this.passengerMarkers.forEach((m) => this.removeOldJob(m));
        this.passengerMarkers = [];
      }
    },

    removeDriversForObserver() {
      this.driversMarkers.forEach((m) => this.vehiclesLayer.removeLayer(m)); // first remove previous markers
      this.driversMarkers = []; // clear array
    },

    driverRejected(t) {
      if (this.rejectedJobs.find((rt) => rt === t.id)) return true;
      return false;
    },

    drawPassengersForDriver() {
      //   console.log("draw passengers", this.jobs);
      this.removeOldJobs();
      this.jobs.forEach((t) => {
        // var latLng = [t.customer.user_state.lat, t.customer.user_state.lon];
        var coords = this.ditherCoords(t.data.pickup_stop); // fake passenger location
        if (this.passengerMarkers.find((m) => m.id === t.id)) {
          // if passenger marker exists just update it or remove it
          var m = this.passengerMarkers.find((m) => m.id === t.id);
          m.transaction = t; // update transaction on marker
          m.slideTo(coords, this.slideOpts); // m.setLatLng(coords);
          m.setIcon(this.coloredIcon(t));
          this.drawPassengerToPickupLine(m);
          var removalStates = ["alighted", "acknowledged"]; // "enroute"
          if (!t.data.dropoff_stop) removalStates.push("enroute");
          if (removalStates.includes(t.state) || this.driverRejected(t)) {
            this.stateLayer.removeLayer(m.driverLine);
            this.stateLayer.removeLayer(m.pickup);
            this.stateLayer.removeLayer(m.dropoff);
            this.stateLayer.removeLayer(m.line);
            this.stateLayer.removeLayer(m);
          }
          if (t.data.dropoff_stop && t.state === "enroute") {
            if (!m.dropoff) {
              this.stateLayer.removeLayer(m.driverLine);
              this.stateLayer.removeLayer(m);
              this.stateLayer.removeLayer(m.pickup);
              this.drawPassengerDropoffPoint(m);
              this.fitToLayer(this.stateLayer);
            }
          }
        } else {
          // otherwise need to add the marker
          // if (t.state === "enroute" || t.state === "acknowledged") return;
          var skipStates = ["alighted", "acknowledged"];
          if (!t.data.dropoff_stop) skipStates.push("enroute");
          if (skipStates.includes(t.state)) return;
          var c = L.marker(coords, {
            icon: this.coloredIcon(t),
            draggable: true,
          }).addTo(this.stateLayer);
          var profPic = this.profPic(t.customer);
          var name = this.shortenString(t.customer.name, 20);
          var passenger = this.$t("passenger.passenger");
          if (this.isDelivery(t)) {
            if (t.state === "enroute" || t.state === "alighted") {
              passenger = this.$t("customer");
            } else {
              passenger = this.$t("Shop");
              profPic = t.business.profile_pic_url;
              name = this.shortenString(t.business.name, 20);
            }
          }
          var img = `<img src=${profPic} style="object-fit: cover; height: 90px; width: 90px;" />`;

          var content = `<div style="width:90px">${img}<i>${passenger}</i><br>${name}</div>`;
          var popup = L.popup(this.popupNarrow).setContent(content);
          c.bindPopup(popup);
          c.customer = t.customer;
          c.id = t.id;
          c.transaction = t;
          this.drawPassengerToPickupLine(c);
          this.drawPassengerPickupPoint(c);
          this.passengerMarkers.push(c);
          this.console("customer marker", c);
        }
      });
      // this.drawDriverLines();
    },

    // if data.predicted_route
    // drawDrivingRoute(encoded) {
    //   var polyUtil = require("polyline-encoded");
    //   var array = polyUtil.decode(encoded);
    //   var polyline = new L.polyline(array, {
    //     color: "#0394fc",
    //     weight: 5,
    //     opacity: 0.6,
    //   });
    //   polyline.addTo(this.map);
    // },

    drawDriverLines() {
      if (!this.driverMarker) return;
      this.passengerMarkers.forEach((p) => {
        if (p.driverLine) this.stateLayer.removeLayer(p.driverLine); // remove old line
        if (p.transaction.state.includes("cancelled_")) return; // don't draw line for cancelled states
        var latlngs = Array();
        latlngs.push(p.point.getLatLng());
        latlngs.push(this.driverMarker.getLatLng());
        var states = ["enroute", "driver_arrived"];
        var encoded = p.transaction.data.predicted_route;
        var style = this.straightLineOpt;
        if (states.includes(p.transaction.state) && encoded) {
          console.log("state", p.transaction.state);
          var polyUtil = require("polyline-encoded");
          latlngs = polyUtil.decode(encoded);
          style = this.drivingLineOpt;
        }
        p.driverLine = L.polyline(latlngs, style);
        p.driverLine.addTo(this.stateLayer);
        var debugTxt = `<i>${p.transaction.state}</i><br>${p.customer.name}`;
        p.driverLine.bindPopup(debugTxt, this.popupWide);

        this.redrawLineOnZoom(p.driverLine);
      });
    },

    drawUserMarker() {
      var coords = this.$store.getters.getUserLocation;
      if (!coords) return console.warn("drawUserMarker getUserLocation null");
      var opts = {
        radius: 10,
        stroke: false,
        fillColor: "#ffc0cb",
        fillOpacity: 0.2,
      };
      this.userMarker = L.circle(coords, opts).addTo(this.stateLayer); // draw faint pink circle
      var updatedAt = this.hhmmTime(this.$store.state.userState.updated_at);
      this.userMarker.bindPopup(`Updated ${updatedAt}`, this.popupWide);
      this.map.on("locationfound", (e) => {
        if (this.userMarker) {
          this.userMarker.setLatLng(e.latlng);
          var updatedAt = this.hhmmTime(new Date());
          this.userMarker.bindPopup(`Updated ${updatedAt}`, this.popupWide);
        }
      });
    },

    drawDriverForDriver() {
      if (!this.driverMarker) this.initialiseDriverForDriver();
      var us = this.$store.state.userState;
      this.driverMarker.slideTo(us, this.slideOpts);
      var profPic = this.profPic();
      var img = `<img src=${profPic} style="object-fit: cover; height: 90px; width: 90px;" />`;
      var you = this.$t("you");
      var driver = this.$t("driver.driver");
      var content = `<div style="width:90px;">${img}<center><strong>${you}</strong><br>${driver}</center></div>`;
      this.driverMarker.bindPopup(content, this.popupNarrow);
      // update hidden driver marker
      this.hiddenDriverMarker.setLatLng(us);
    },

    initialiseDriverForDriver() {
      var driver = L.divIcon({
        html: `<div class='driver-circle cyan-bg'><img class='driver-big' src=${this.icon} /></div>`,
        popupAnchor: [-6, -28],
        className: "none",
      });
      var icon = { icon: driver, draggable: true };
      var coords = this.$store.getters.getUserLocation;
      this.driverMarker = L.marker(coords, icon);
      this.driverMarker.addTo(this.stateLayer);

      // also add a hidden driver marker to keep driver in view with fleet
      var hidden = { radius: 0, opacity: 0, fillOpacity: 0 };
      this.hiddenDriverMarker = L.circle(coords, hidden);
      this.hiddenDriverMarker.addTo(this.vehiclesLayer); // add to vehiclesLayer
    },

    // this tracks all drivers for workflow, could use `nearby` or `sii lor` etc.
    updateDriversForObserver() {
      this.$store
        .dispatch("trackDrivers", this.workflow.id)
        .then((driverStates) => this.drawDriversForObserver(driverStates))
        .catch((err) => console.warn("trackDrivers", err));
    },

    drawDriversForObserver(drivers) {
      if (!drivers || drivers.length === 0) return;
      this.removeDriversForObserver(); // clears existing vehicles
      var availableIcon = L.divIcon({
        html: `<div class='driver-circle available-bg'><img class='driver-small' src='${this.icon}' /></div>`,
        popupAnchor: [-5, -20],
        className: "none",
      });
      var busyIcon = L.divIcon({
        html: `<div class='driver-circle busy-bg'><img class='driver-small' src='${this.icon}' /></div>`,
        popupAnchor: [-5, -20],
        className: "none",
      });
      var available = { icon: availableIcon, draggable: true };
      var busy = { icon: busyIcon, draggable: true };

      drivers.forEach((ds) => {
        if (ds.id === this.userID()) return; // skip yourself
        // if (ds.role.role_name === "Dispatcher") return; // skip dispatcher
        if (this.farAwayFromUser(ds, 3000)) return; // fixes bad driver location also
        var t = (new Date() - new Date(ds.updated_at)) / (1000 * 60); // minutes ago
        if (t < 0) return 0;
        if (t > 240) return; // don't show if older than an hour or 4 hours
        var options = available;
        if (ds.role.data.is_busy) options = busy;
        var m = L.marker([ds.lat, ds.lon], options).addTo(this.vehiclesLayer);
        var name = `<strong>${ds.user.name}</strong>`;
        var text = `${name}<br>${Math.round(t)} mins ago`;
        var content = `<a>${text}</a>`; //`<a onclick="selectDriver('${ds.id}')">${text}</a>`;
        m.bindPopup(content, this.popupNarrow);
        this.driversMarkers.push(m);
      });
    },

    farAwayFromUser(p, dist) {
      if (this.$store.getters.isSuperUser) return false; // show all drivers to superuser
      if (this.isDispatcher) return false; // show all drivers to dispatcher
      var coords = this.$store.getters.getUserLocation;
      if (!coords) return false;
      var userLoc = { lat: coords[0], lon: coords[1] };
      if (geo.distanceToUserInMeters(p, userLoc) > dist) return true;
      return false;
    },

    _() { }, ///////////////// END METHODS /////////////////
  },
};
</script>

<style scoped>
.map-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  /* height: calc(100vh - 215px); */
  width: 100vw;
}

.map {
  height: calc(100vh - 175px);
  width: 100vw;
  z-index: 0;
}

.dispatcher-btn {
  background-color: orange;
  color: white;
}

.driver-icon {
  -webkit-filter: drop-shadow(2px 2px 2px #666);
  filter: drop-shadow(2px 2px 2px #666);
  box-shadow: 4px 4px 3px grey;
  -moz-box-shadow: 4px 4px 3px grey;
  -webkit-box-shadow: 4px 4px 3px grey;
}

.truncate {
  /* max-width: 100px; */
  max-width: calc(100vw - 160px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.top-right {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

.top-right p {
  margin: 10px;
  display: block;
  padding: 5px 12.5px;
  filter: drop-shadow(2px 2px 2px #666);
}

.bullseye-btn {
  padding: 5px 12.5px;
}

.bullseye {
  content: url("../assets/bullseye.png");
}

/* .leaflet-top {
  pointer-events: auto;
}
.leaflet-right p {
  margin: 10px;
  display: block;
  padding: 5px 12.5px;
  filter: drop-shadow(2px 2px 2px #666);
} */
.header {
  /* width: calc(100vw - 72px); */
  width: 100vw;
  padding: 10px 66px;
}

.bg-green {
  background-color: palegreen;
}

.fa-kiwi-bird {
  margin-bottom: 10px;
  font-size: 5em;
  color: brown;
}
</style>

<style lang="scss">
#driver-map {
  .leaflet-popup-content img {
    border-radius: 8px;
    box-shadow: 1px 1px 9px #ccc;
  }

  .leaflet-popup-content i {
    font-weight: 500;
    opacity: 0.8;
    color: grey;
  }

  .purple-stop {
    height: 18px;
    width: 18px;
    border-radius: 50%;
    filter: drop-shadow(2px 2px 2px #555);
    background-color: magenta;
    opacity: 0.9;
    border-style: solid;
    border-width: 1px;
    border-color: darkslategrey;
    /* animation: shadow-pulse 1s 999; */
  }

  .dropoff-point {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    /* transform: translateX(-50%) translateY(-50%); */
    filter: drop-shadow(2px 2px 2px #555);
    background-color: rgb(0, 89, 255);
    border-style: solid;
    border-width: 0.5px;
    border-color: grey;
    animation: shadow-pulse 1s 999;
  }

  .pickup-point {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    /* transform: translateX(-50%) translateY(-50%); */
    filter: drop-shadow(2px 2px 2px #555);
    background-color: lightgreen;
    border-style: solid;
    border-width: 0.5px;
    border-color: grey;
    animation: shadow-pulse 1s 999;
  }

  .bg-red {
    background-color: red;
  }

  @keyframes shadow-pulse {
    0% {
      box-shadow: 0 0 0 0px rgba(247, 247, 8, 0.95);
    }

    // 50% {
    //   box-shadow: 0 0 0 5px rgba(0, 255, 247, 0.5);
    // }
    100% {
      box-shadow: 0 0 0 10px rgba(0, 255, 247, 0.1);
    }
  }

  .driver-circle {
    /* for passenger */
    height: 43px;
    width: 43px;
    border-radius: 50%;
    padding: 4px;
    transform: translateX(-50%) translateY(-50%);
    filter: drop-shadow(2px 2px 2px #555);
    background-color: rgba(255, 255, 0, 0.651);
    // border-style: solid;
    // border-width: 0.5px;
    // border-color: grey;
    border: 0.5px solid grey;
    animation: shadow-pulse 1s infinite ease alternate;
  }

  .cyan-bg {
    /* for driver */
    background-color: cyan;
  }

  .opaque-bg {
    height: 30px;
    width: 30px;
    background-color: rgba(255, 255, 255, 0.7);
    border-width: 0px;
  }

  .available-bg {
    height: 30px;
    width: 30px;
    // background-color: rgba(159, 255, 155, 0.7);
    background-color: palegreen;
    border-width: 0px;
  }

  .busy-bg {
    height: 30px;
    width: 30px;
    background-color: rgba(255, 94, 201, 0.7);
    border-width: 0px;
  }

  .driver-small {
    height: 20px;
    width: 20px;
    filter: drop-shadow(0px 0px 1px #555);
  }

  .driver-big {
    height: 30px;
    width: 30px;
    filter: drop-shadow(0px 0px 1px #555);
  }

  .none {
    /* empty on purpose to hide small white box under divIcon */
  }

  .leaflet-popup-content {
    padding: 5px 5px;
    margin: 1px 0;
  }
}
</style>